<template>
  <div class="ll-shell" :class="{ 'no-pad': noPad }">
    <!-- Header -->
    <header class="header" :class="{ 'header-compact': compactHeader }">
      <div class="brand">
        <slot name="logo">
          <!-- Optional default logo placeholder area (empty unless provided) -->
        </slot>

        <div class="brand-text">
          <h1 class="page-heading">{{ title }}</h1>
          <div v-if="subtitle" class="subtitle">{{ subtitle }}</div>
        </div>
      </div>

      <div class="header-actions">
        <slot name="actions"></slot>
      </div>
    </header>

    <!-- Main content -->
    <main class="content" :class="{ 'content-wide': wide }">
      <!-- Default: wrap in a glass card -->
      <section v-if="card" class="card" :class="{ 'card-tight': tightCard }">
        <slot></slot>
      </section>

      <!-- Optional: no card wrapper -->
      <slot v-else></slot>
    </main>

    <!-- Footer -->
    <footer class="footer" :class="{ 'footer-hidden': hideFooter }">
      <slot name="footer">
        <div class="foot-left">© {{ new Date().getFullYear() }} LotLedger</div>
        <div class="foot-right">
          <router-link class="foot-link" to="/system">System</router-link>
          <span class="dot">•</span>
          <router-link class="foot-link" to="/reports-dashboard">Reports</router-link>
        </div>
      </slot>
    </footer>
  </div>
</template>

<script>
export default {
  name: "PageShell",
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },

    // Layout behavior
    card: { type: Boolean, default: true },        // Wrap body in a glass card
    tightCard: { type: Boolean, default: false },  // Less padding in the card
    wide: { type: Boolean, default: false },       // Slightly wider content max
    noPad: { type: Boolean, default: false },      // Remove outer padding (rare)
    compactHeader: { type: Boolean, default: false },
    hideFooter: { type: Boolean, default: false },
  },
};
</script>

<style scoped>
.ll-shell {
  min-height: 100vh;
  font-family: "Poppins", sans-serif;
  background: linear-gradient(180deg, #9cd0f2 0%, #eef4fb 55%, #ffffff 100%);
  padding: 22px;
  color: #1f2a35;
  display: flex;
  flex-direction: column;
}

.ll-shell.no-pad {
  padding: 0;
}

/* Header */
.header {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(131, 167, 203, 0.8);
  border-radius: 18px;
  padding: 16px 18px;
  box-shadow: 0px 6px 18px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.header.header-compact {
  padding: 12px 14px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-text {
  display: flex;
  flex-direction: column;
}

.page-heading {
  margin: 0;
  font-size: 26px;
  letter-spacing: 0.5px;
  font-weight: 900;
}

.subtitle {
  margin-top: 2px;
  font-size: 13px;
  opacity: 0.75;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* Main */
.content {
  margin-top: 16px;
  display: grid;
  gap: 12px;
  flex: 1;
  max-width: 1100px;
}

.content.content-wide {
  max-width: 1280px;
}

.card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 16px;
}

.card.card-tight {
  padding: 12px;
}

/* Footer */
.footer {
  margin-top: 18px;
  padding: 10px 4px;
  display: flex;
  justify-content: space-between;
  opacity: 0.75;
  font-weight: 700;
}

.footer.footer-hidden {
  display: none;
}

.foot-link {
  text-decoration: none;
  color: inherit;
}
.foot-link:hover {
  text-decoration: underline;
}
.dot {
  margin: 0 8px;
}

/* Mobile */
@media (max-width: 980px) {
  .ll-shell {
    padding: 16px;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    justify-content: stretch;
  }
}
</style>
